import { useState } from "react";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

const Login2FAForm = ({ email, emailSent, displayCode, onVerified, onBack }) => {
  const { verifyLogin } = useAuth();
  const [codeInput, setCodeInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!codeInput.trim()) { setError("Please enter the verification code"); return; }
    setLoading(true);
    try {
      await verifyLogin(email, codeInput.trim());
      onVerified();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={onBack} className="flex items-center gap-1 mb-4 text-sm" style={{ color: "var(--color-text-dim)" }}>
        <ArrowLeft className="w-4 h-4" /> Back to Login
      </button>

      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
        <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Verify Your Identity
        </h2>
      </div>

      {emailSent ? (
        <p className="mb-4" style={{ color: "var(--color-text-muted)" }}>
          A 6-digit verification code has been sent to <strong style={{ color: "var(--color-text)" }}>{email}</strong>. Check your inbox to complete sign in.
        </p>
      ) : displayCode ? (
        <>
          <p className="mb-3" style={{ color: "var(--color-text-muted)" }}>
            Email delivery failed. Use the code below to sign in:
          </p>
          <div className="mb-4 p-4 rounded-lg text-center" style={{ backgroundColor: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}>
            <p className="text-xs mb-1" style={{ color: "var(--color-text-dim)" }}>Your login code</p>
            <p className="text-3xl font-black tracking-[0.3em]" style={{ color: "var(--color-accent)" }} data-testid="login-2fa-code-display">
              {displayCode}
            </p>
          </div>
        </>
      ) : (
        <p className="mb-4" style={{ color: "var(--color-text-muted)" }}>
          Check <strong style={{ color: "var(--color-text)" }}>{email}</strong> for your login verification code.
        </p>
      )}

      <div className="mb-6 p-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
        <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>
          This code expires in 5 minutes. If you didn't request this, your password may be compromised.
        </p>
      </div>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="login-2fa-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Enter Code</label>
          <Input type="text" value={codeInput} onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000" className="text-center text-2xl tracking-[0.3em] rounded-lg font-bold"
            style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
            data-testid="login-2fa-code-input" maxLength={6} required />
        </div>
        <Button type="submit" disabled={loading || codeInput.length !== 6}
          className="w-full rounded-lg py-3 font-bold text-white"
          style={{ backgroundColor: "var(--color-accent)" }} data-testid="login-2fa-verify-button">
          {loading ? "Verifying..." : "Verify & Sign In"}
        </Button>
      </form>
    </>
  );
};

export default Login2FAForm;
